import type { ReactNode } from "react";
import styled from "@emotion/styled";

import MemProactiveWelcomeImage from "@/assets/images/mem-proactive-redirect.svg";
import { PROACTIVE_MONEY_BACK_GUARANTEE_NOTE } from "@/components/modal/proactive-upsell/proactive-plan";
import { ProactiveTrialPitch } from "@/components/proactive-trial/proactive-trial-pitch";
import {
  FooterSkipButton,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import {
  MDSButton,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
export {
  ExistingUserMemAgentTransitionStepView,
  ExistingUserPushToRememberStepView,
} from "./existing-user-mem-agent-step-views";
export type {
  ExistingUserMemAgentTransitionStepViewProps,
  ExistingUserPushToRememberStepViewProps,
} from "./existing-user-mem-agent-step-views";

const EXISTING_USER_PROACTIVE_TRIAL_TITLE = "Mem Agent is better with Proactive";

export interface ExistingUserProactiveTrialStepViewProps {
  canSkip: boolean;
  isActionLoading?: boolean;
  isTrialEligible: boolean;
  isWindows?: boolean;
  planFooter?: ReactNode;
  proactivePrice?: string;
  pricingError?: string;
  pricingIsLoading?: boolean;
  onLearnMore: () => void;
  onRetryPricing: () => void;
  onSkip: () => void;
  onSubscribe: () => void;
}

/** Presents the existing-user Proactive offer without adding it to the onboarding pipeline. */
export const ExistingUserProactiveTrialStepView = ({
  canSkip,
  isActionLoading = false,
  isTrialEligible,
  isWindows,
  planFooter,
  proactivePrice,
  pricingError,
  pricingIsLoading = false,
  onLearnMore,
  onRetryPricing,
  onSkip,
  onSubscribe,
}: ExistingUserProactiveTrialStepViewProps) => {
  const resolvedPlanFooter =
    planFooter ??
    (canSkip ? (
      <ExistingUserSkipFooter>
        <ExistingUserSkipButton onClick={onSkip}>Skip for now</ExistingUserSkipButton>
      </ExistingUserSkipFooter>
    ) : null);

  return (
    <WizardContentWrapper>
      <ProactiveTrialPitch
        actionNote={PROACTIVE_MONEY_BACK_GUARANTEE_NOTE}
        heading={EXISTING_USER_PROACTIVE_TRIAL_TITLE}
        isActionLoading={isActionLoading}
        isTrialEligible={isTrialEligible}
        isWindows={isWindows}
        planFooter={resolvedPlanFooter}
        proactivePrice={proactivePrice}
        pricingError={pricingError}
        pricingIsLoading={pricingIsLoading}
        onLearnMore={onLearnMore}
        onRetryPricing={onRetryPricing}
        onSubscribe={onSubscribe}
      />
    </WizardContentWrapper>
  );
};

export interface ExistingUserWelcomeToProactiveModalProps {
  isOpen: boolean;
  onContinue: () => void;
}

/** Modal-sized welcome surface for callback-triggered Proactive onboarding moments. */
export const ExistingUserWelcomeToProactiveModal = ({
  isOpen,
  onContinue,
}: ExistingUserWelcomeToProactiveModalProps) => (
  <ExistingUserWelcomeModal
    isOpen={isOpen}
    isDismissable={false}
    handleClose={onContinue}
    fluidWidth
  >
    <div slot={MDSDialogSlotName.Header} />
    <ExistingUserWelcomeContent slot={MDSDialogSlotName.Content}>
      <ExistingUserWelcomeIllustration src={MemProactiveWelcomeImage} alt="" aria-hidden />
      <ExistingUserWelcomeTitle
        variant={MDSTextVariant.HeaderL}
        weight={MDSTextWeight.SemiBold}
        color={MDSTextColor.Contrast}
      >
        Welcome to Mem Proactive
      </ExistingUserWelcomeTitle>
    </ExistingUserWelcomeContent>
    <ExistingUserWelcomeFooter slot={MDSDialogSlotName.Footer}>
      <MDSButton variant={MDSButtonVariant.Contrast} onPress={onContinue}>
        Continue
      </MDSButton>
    </ExistingUserWelcomeFooter>
  </ExistingUserWelcomeModal>
);

const ExistingUserSkipFooter = styled.div`
  display: flex;
  justify-content: center;
`;

const ExistingUserSkipButton = styled(FooterSkipButton)`
  align-self: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const ExistingUserWelcomeModal = styled(MDSModalDialog)`
  .mds-dialog {
    box-sizing: border-box;
    width: min(421px, calc(100vw - 32px));
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]} ${theme.spacing[7]}`};
    gap: 0;
  }

  .mds-dialog [data-mds-name="dialog-body"] {
    overflow: hidden;
    scrollbar-gutter: auto;
  }
`;

const ExistingUserWelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(366px, 100%);
  gap: ${({ theme }) => theme.spacing[3]};
  text-align: center;
`;

const ExistingUserWelcomeIllustration = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const ExistingUserWelcomeTitle = styled(MDSText)`
  display: block;
`;

const ExistingUserWelcomeFooter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: ${({ theme }) => theme.spacing[3]};
`;
